/**
 * <s-dert-currency> — display-only currency switcher.
 * Picks USD / GBP / EUR from the visitor's country (or their saved choice), loads live
 * rates from Shopify's CDN and rewrites every [data-dert-money] amount (cents in the
 * store currency). Re-applies when the cart or variant prices re-render.
 * Checkout still charges in the store currency; [data-dert-currency-note] explains that.
 */
const RATES_URL = 'https://cdn.shopify.com/s/javascripts/currencies.js';
const STORAGE_KEY = 'dert-currency';
const EU = new Set(['AT','BE','BG','HR','CY','CZ','DE','DK','EE','FI','FR','GR','HU','IE','IT','LV','LT','LU','MT','NL','PL','PT','RO','SK','SI','ES','SE']);

let ratesPromise = null;

/** Live rates: value of one unit of each currency in USD. */
function loadLiveRates() {
  if (window.Currency && window.Currency.rates) return Promise.resolve(window.Currency.rates);
  if (!ratesPromise) {
    ratesPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = RATES_URL;
      script.async = true;
      script.onload = () => (window.Currency && window.Currency.rates ? resolve(window.Currency.rates) : reject(new Error('no rates')));
      script.onerror = () => reject(new Error('rates failed'));
      document.head.append(script);
      setTimeout(() => reject(new Error('rates timeout')), 8000);
    });
  }
  return ratesPromise;
}

class SDertCurrency extends HTMLElement {
  #select = null;
  #shop = 'ZAR';
  /** Multiplier from store currency to each display currency. */
  #factors = {};
  #observer = null;
  #frame = 0;

  connectedCallback() {
    this.#select = this.querySelector('select');
    this.#shop = this.dataset.shopCurrency || 'ZAR';
    if (!this.#select) return;

    loadLiveRates()
      .then((rates) => this.#useLiveRates(rates))
      .catch(() => this.#useBackupRates())
      .then((ok) => (ok ? this.#start() : this.remove()));
  }

  disconnectedCallback() {
    if (this.#observer) this.#observer.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  #useLiveRates(rates) {
    const shopRate = Number(rates[this.#shop]);
    if (!shopRate) return this.#useBackupRates();
    for (const option of this.#select.options) {
      const rate = Number(rates[option.value]);
      if (option.value === this.#shop) this.#factors[option.value] = 1;
      else if (rate) this.#factors[option.value] = shopRate / rate;
    }
    return true;
  }

  /** Merchant-entered backup rates: store currency per one unit of the display currency. */
  #useBackupRates() {
    this.#factors[this.#shop] = 1;
    for (const code of ['USD', 'EUR', 'GBP']) {
      const perUnit = parseFloat(String(this.dataset['rate' + code.charAt(0) + code.slice(1).toLowerCase()] || '').replace(',', '.'));
      if (perUnit > 0) this.#factors[code] = 1 / perUnit;
    }
    return Object.keys(this.#factors).length > 1;
  }

  #defaultCurrency() {
    let saved = null;
    try { saved = localStorage.getItem(STORAGE_KEY); } catch (e) { /* private mode */ }
    if (saved && this.#factors[saved]) return saved;
    const country = (this.dataset.country || '').toUpperCase();
    let code = 'USD';
    if (country === 'GB') code = 'GBP';
    else if (EU.has(country)) code = 'EUR';
    else if (country === 'ZA') code = this.#shop;
    return this.#factors[code] ? code : this.#shop;
  }

  #start() {
    for (const option of [...this.#select.options]) {
      if (!this.#factors[option.value]) option.remove();
    }
    this.#select.value = this.#defaultCurrency();
    this.hidden = false;

    this.#select.addEventListener('change', () => {
      try { localStorage.setItem(STORAGE_KEY, this.#select.value); } catch (e) { /* private mode */ }
      this.#apply();
    });

    this.#apply();
    this.#observer = new MutationObserver(() => {
      cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(() => this.#apply());
    });
    this.#observer.observe(document.body, { childList: true, subtree: true, characterData: true });
  }

  #apply() {
    const code = this.#select.value;
    const factor = this.#factors[code] || 1;
    const lang = document.documentElement.lang || 'en';
    let format;
    try {
      format = new Intl.NumberFormat(lang, { style: 'currency', currency: code });
    } catch (e) {
      format = new Intl.NumberFormat('en', { style: 'currency', currency: code });
    }

    for (const el of document.querySelectorAll('[data-dert-money]')) {
      const cents = el.getAttribute('data-dert-money');
      if (el.dataset.dertFor !== cents) {
        el.dataset.dertFor = cents;
        el.dataset.dertOriginal = el.innerHTML;
        delete el.dataset.dertShown;
      }
      if (code === this.#shop) {
        if (el.dataset.dertShown && el.dataset.dertShown !== this.#shop) el.innerHTML = el.dataset.dertOriginal;
        el.dataset.dertShown = this.#shop;
        continue;
      }
      const text = format.format((Number(cents) / 100) * factor);
      if (el.textContent !== text) el.textContent = text;
      el.dataset.dertShown = code;
    }

    for (const note of document.querySelectorAll('[data-dert-currency-note]')) {
      const hide = code === this.#shop;
      if (note.hidden !== hide) note.hidden = hide;
    }
  }
}

if (!customElements.get('s-dert-currency')) customElements.define('s-dert-currency', SDertCurrency);
